import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { usePayPointScanViewQuery } from "Api/Queries/PayPoints/usePayPointScanViewQuery";
import { VariableCollectorPaymentForm } from "Components/Customer/Payment/PaymentForms/VariableCollectorPaymentForm";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { ErrorWithRetry } from "Components/Shared/Errors/ErrorWithRetry";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import type { ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import { useParams } from "react-router-dom";

const PageResources = Resources.Customer.Pay;

export const VariableCollectorPaymentPage = () => {
  const { t } = useResource();
  const { id: paypointPublicID } = useParams<{ id: string }>();

  const {
    data: paypoint,
    isLoading: isLoadingPayPoint,
    isError: hasPayPointLoadFailed,
    refetch: retryPayPointLoad,
  } = usePayPointScanViewQuery(paypointPublicID);

  const {
    data: bankAccountsData,
    isLoading: isLoadingBankAccounts,
    isError: hasBankAccountsLoadFailed,
    refetch: retryBankAccountsLoad,
  } = useBankingAccounts();

  if (!paypointPublicID) {
    return (
      <Layout>
        <ErrorText>{t(Resources.Error.InvalidPaypointId)}</ErrorText>
      </Layout>
    );
  }
  const isLoading = isLoadingPayPoint || isLoadingBankAccounts;

  if (isLoading) {
    return (
      <Layout>
        <CenteredCircularProgress />
      </Layout>
    );
  }

  if (hasPayPointLoadFailed || !paypoint) {
    return (
      <Layout>
        <ErrorWithRetry
          message={t(Resources.Errors.DataLoadingError)}
          onRetry={() => retryPayPointLoad()}
        />
      </Layout>
    );
  }

  if (hasBankAccountsLoadFailed || !bankAccountsData) {
    return (
      <Layout>
        <ErrorWithRetry
          message={t(Resources.Errors.DataLoadingError)}
          onRetry={() => retryBankAccountsLoad()}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <VariableCollectorPaymentForm
        paypointPublicID={paypointPublicID}
        paypoint={paypoint}
        bankAccounts={bankAccountsData.items}
      />
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();

  return (
    <AuthenticatedLayout
      title={t(PageResources.Title)}
      backNavigation={{ mode: "staticUrl", url: appUrl("dashboard") }}
      isBottomMenuHidden
    >
      <StyledPageWrapper>{children}</StyledPageWrapper>
    </AuthenticatedLayout>
  );
};
